import React, { useState } from 'react';
import { Box, ButtonBase, Link, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { policyHref } from '../Ontology/ontologyFormat';
import { FullDecision, alternativesOf, confidenceLabel } from './explainabilityTypes';
import { dsVar, ink } from '../../theme/tokens';

/**
 * What surrounds a decision's rationale: its confidence, the policies it cites,
 * and — behind a toggle — why its technique was chosen and what it turned down.
 * Styled with sx: it renders inside a portalled drawer.
 */

const pill = { fontSize: '0.625rem', fontWeight: 600, borderRadius: '4px', px: 0.75, py: 0.125, whiteSpace: 'nowrap' as const };

/** `Confidence 82%`, for the decision's title row; nothing when unrecorded. */
export const ConfidencePill: React.FC<{ score?: number }> = ({ score }) => {
    const label = confidenceLabel(score);
    if (!label) return null;
    return (
        <Box component="span" title="How confident the agent was in this decision" sx={{ ...pill, color: dsVar('fg-soft'), border: `1px solid ${dsVar('line')}` }}>
            Confidence {label}
        </Box>
    );
};

/** Policies a decision cites, each linking to its page in the ontology. */
export const PolicyRefs: React.FC<{ codes?: string[] }> = ({ codes }) => {
    if (!codes?.length) return null;
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
            <Typography component="span" sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>Cites</Typography>
            {codes.map((code) => {
                const href = policyHref(code);
                return href ? (
                    <Link key={code} href={href} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ ...pill, fontFamily: '"JetBrains Mono", monospace', color: ink('#7DD3FC'), border: '1px solid rgba(125, 211, 252, 0.3)' }}>
                        {code}
                    </Link>
                ) : (
                    <Box key={code} component="span" sx={{ ...pill, fontFamily: '"JetBrains Mono", monospace', color: dsVar('fg-2'), border: `1px solid ${dsVar('line')}` }}>{code}</Box>
                );
            })}
        </Box>
    );
};

/** Why the chosen technique, and what else was weighed, collapsed until asked. */
export const Alternatives: React.FC<{ decision: FullDecision }> = ({ decision }) => {
    const [open, setOpen] = useState(false);
    const alternatives = alternativesOf(decision);
    const chosenReason = decision.selected_technique?.reason;
    if (alternatives.length === 0 && !chosenReason) return null;

    const toggleLabel = alternatives.length > 0
        ? `${alternatives.length} ${alternatives.length === 1 ? 'alternative' : 'alternatives'} considered`
        : 'Why this technique';

    return (
        <Box sx={{ mt: 0.75 }}>
            <ButtonBase
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                sx={{ gap: 0.5, fontSize: '0.75rem', color: dsVar('fg-2'), py: 0.25, '&:hover': { color: dsVar('fg-body') } }}
            >
                <ExpandMoreIcon sx={{ fontSize: 16, transition: 'transform 150ms', transform: open ? 'none' : 'rotate(-90deg)' }} />
                {toggleLabel}
            </ButtonBase>
            {open && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.75, pl: 2.5 }}>
                    {decision.selected_technique && (
                        <Box>
                            <Typography sx={{ fontSize: '0.75rem', color: ink('#34D399'), fontWeight: 600 }}>
                                ✓ {decision.selected_technique.name}
                            </Typography>
                            {chosenReason && (
                                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), lineHeight: 1.6 }}>{chosenReason}</Typography>
                            )}
                        </Box>
                    )}
                    {alternatives.map((alt) => (
                        <Box key={alt.name}>
                            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-soft'), fontWeight: 600 }}>✕ {alt.name}</Typography>
                            {alt.reason && (
                                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), lineHeight: 1.6 }}>{alt.reason}</Typography>
                            )}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};
